import {Injectable} from '@angular/core';
import {catchError, from, map, Observable, of, throwError} from "rxjs";
import {Event} from "../models/event"
import {AuthenticationService} from "./authentication.service";
import {ImageService} from "./image.service";
import {User} from "../models/user";
import {
  addDoc,
  collection,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  Timestamp,
  Query,
  QuerySnapshot,
  DocumentData,
  doc,
  getDoc,
  DocumentSnapshot, DocumentReference, setDoc, deleteDoc
} from "@angular/fire/firestore";
import {Accessibility} from "../models/accessibility";
import {ACCESSIBILITY_TAGS} from "../constants/accessibility-tags";

@Injectable({
  providedIn: 'root'
})
export class EventService {
  events: Event[] = [];
  private event_get_limit!: number;

  constructor(
    private firestore: Firestore,
    private authService: AuthenticationService,
    private imgService: ImageService
  ) {}

  /**
   * Loop through all the data and subscribe to any changes
   * @private
   */
  private fireGetEvents(): Observable<Event[]> {
    this.events = [];

    const eventsQuery = query(
      collection(this.firestore, "events"), limit(this.event_get_limit), orderBy("start", "desc")
    ) as Query<DocumentData>;

    from(getDocs(eventsQuery)).subscribe(data => data.forEach(doc => {
      let newDoc = {
        id: doc.id,
        ...doc.data(),
        accessibility: Object.assign(
          new Accessibility(),
          doc.get('accessibility')
        )
      };
      this.events.push(newDoc as Event);
    }));

    return of(this.events);
  }

  /**
   * Initiate the service by setting a default limit value and getting the data from the database
   * @param outputLimit number, default 5
   */
  public init(outputLimit: number = 5) {
    this.event_get_limit = outputLimit;
    this.fireGetEvents();
  }

  /**
   * Set limit is used to change the limit of events to be fetched
   * and re-fetches after the limit is changed
   * @param limit number
   */
  public setLimit(limit: number) {
    this.event_get_limit = limit;
    this.fireGetEvents();
  }

  /**
   * Deletes an event and corresponding registrations.
   *
   * @param eventID
   */
  async deleteEvent (eventID: string): Promise<void> {
    const registrationsRef = collection(this.firestore, "registrations");
    const eventRef = doc(this.firestore, "events", eventID);
    const regQuery = query(registrationsRef, where("event", "==", eventRef));

    const querySnapshot = await getDocs(regQuery);

    const deletePromises = querySnapshot.docs.map((document) =>
      deleteDoc(doc(this.firestore, "registrations", document.id))
    );

    await Promise.all(deletePromises);
    console.log(`${querySnapshot.size} registratie(s) voor activiteit met ID: ${eventID} verwijderd.`);

    await deleteDoc(eventRef);
    console.log(`Activiteit met ID: ${eventID} is verwijderd.`)
  }

  /**
   * Creates a new event with form data and returns the document ID of the newly created event.
   *
   * @return document id of newly created event
   * @param eventData
   * @param imageArray
   */
  async createEvent (eventData: Partial<Record<string, any>>, imageArray: File[]): Promise<string> {
    const user = await this.authService.getUser() as User;
    const eventFields = await this.prepareEventData(eventData, imageArray, user);

    const docRef = await addDoc(collection(this.firestore, 'events'), {
      ...eventFields,
      created: Timestamp.now(),
      createdBy: user.uid,
      organization: user.organization
    });

    return docRef.id;
  }

  /**
   * Updates an event with form data and returns the document ID of the updated event.
   *
   * @return document id of newly created event
   * @param eventId
   * @param orgRef
   * @param eventData
   * @param imageArray
   */
  async updateEvent(eventId: string, orgRef: DocumentReference<DocumentData>, eventData: Partial<Record<string, any>>, imageArray: File[]): Promise<string> {
    const user = await this.authService.getUser() as User;
    const eventFields = await this.prepareEventData(eventData, imageArray, user);

    const eventRef = doc(this.firestore, 'events', eventId);

    await setDoc(eventRef, {
      ...eventFields,
      updated: Timestamp.now(),
      updatedBy: user.uid,
      organization: orgRef
    });

    return eventId;
  }

  async prepareEventData(eventData: Partial<Record<string, any>>, imageArray: File[], user: User) {
    // Handle image uploads
    let imgReference: string[] = [];

    if (imageArray.length == 0) {
      imgReference.push("https://firebasestorage.googleapis.com/v0/b/steddie-d5171.appspot.com/o/Standaard-" + eventData['category'] + ".avif?alt=media");
    } else {
      for (const image of imageArray) {
        const imgRef = await this.imgService.uploadToStorage(image["name"], image);
        imgReference.push(imgRef);
      }
    }

    // Construct accessibility object
    const accessibility: Accessibility = ACCESSIBILITY_TAGS.reduce((acc, accessibilityTag) => {
      const checkKey = `${accessibilityTag.tag}Check`;
      acc[accessibilityTag.tag as keyof Accessibility] = eventData[checkKey] || false;
      return acc;
    }, {} as Accessibility);

    // Construct address
    const address = {
      zipcode: eventData['zipcode'],
      houseNumber: eventData['houseNumber'],
      streetName: eventData['streetName'],
      location: eventData['location']
    };

    // Construct start and end times
    const start = new Date(`${eventData['activityDate']}T${eventData['startTime']}`);
    const end = new Date(`${eventData['activityDate']}T${eventData['endTime']}`);

    return {
      name: eventData['name'],
      category: eventData['category'],
      description: eventData['description'],
      accessibility,
      address,
      start,
      end,
      images: imgReference,
      parts: eventData['parts']
    };
  }

  /**
   * Get all the fetched events
   */
  public getEvents() {
    return this.events;
  }

  /**
   * Gets all the events belonging to a specific organization
   *
   *  @param orgRef
   */
  public getEventsByOrganization(orgRef: DocumentReference): Observable<Event[]> {
    const eventsQuery = query(
      collection(this.firestore, 'events'),
      where('organization', '==', orgRef),
      orderBy('start', 'asc')
    ) as Query<DocumentData>;

    return from(getDocs(eventsQuery)).pipe(
      map((snapshot: QuerySnapshot<DocumentData>) => snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        accessibility: Object.assign(
          new Accessibility(),
          doc.get('accessibility')
        )
      }) as Event))
    );
  }

  /**
   * Get specific event based in given document ref
   *
   * @param ref
   */
  public getEventByRef(ref: string): Observable<Event>{
    let docRef;

    if (ref.substring(0, 7) == 'events/') {
      docRef = doc(this.firestore, ref);
    } else {
      docRef = doc(this.firestore, `events/${ref}`);
    }

    return from(getDoc(docRef)).pipe(
      map((snapshot: DocumentSnapshot<DocumentData>) => {
        const data = snapshot.data();
        if (data) {
          return {
            id: snapshot.id,
            ...data,
            accessibility: Object.assign(
              new Accessibility(),
              data['accessibility']
            )
          } as Event;
        } else {
          throw new Error(`Document with reference: ${ref} does not exist`);
        }
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * Counts the amount of documents in the event collection.
   */
  public async countEvents() {
    const eventQuery = query(collection(this.firestore, 'events')) as Query<DocumentData>;

    try {
      const querySnapshot = await getDocs(eventQuery);
      let results: any[] = [];

      querySnapshot.docs.map(doc => {
        results.push(doc.data());
      });

      const uniqueResults = Array.from(
        results.reduce((map, obj) => map.set(obj.address.location, obj), new Map()).values()
      );

      return { users: querySnapshot.size, uniqueLocations: uniqueResults.length };
    } catch (error) {
      console.error("Error executing query and counting results:", error);
      return { users: 0, uniqueLocations: 0 };
    }
  }
}
